import { useCallback, useEffect, useState } from "react"
import Big from "big.js"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable, DataTableExpandedRows, DataTableValueArray } from "primereact/datatable"
import { ProgressSpinner } from "primereact/progressspinner"
import {
    addManualAdjustment,
    getAccountCredits,
    getAccountDebts,
    getAdministrativeCharges,
    getLiquidation,
    getLiquidationDetails,
    getLiquidationPaymentOrders,
    getLiquidationPayments,
    getLiquidationProfessional,
    getLiquidationReceiptForms,
    getManualAdjustments,
    getRetentions,
    removeAdministrativeCharge,
    removeManualAdjustment,
    removeItemFromLiquidation,
    removeRetentions,
} from "../../servicesHooks/Honorarium/useHonorariumLiquidationService"
import { parseApiError } from "../../services/apiError"
import {
    AccountDebtLineDTO,
    HonorariumAdministrativeChargeDTO,
    HonorariumLiquidationAccountCreditDTO,
    HonorariumLiquidationDTO,
    HonorariumLiquidationDetailDTO,
    HonorariumLiquidationDetailGroupDTO,
    HonorariumLiquidationDetailResponseDTO,
    HonorariumLiquidationPaymentDTO,
    HonorariumLiquidationPaymentOrderDTO,
    HonorariumLiquidationProfessionalDTO,
    HonorariumLiquidationStatusId,
    HonorariumManualAdjustmentDTO,
    ManualAdjustmentDTO,
    ManualAdjustmentType,
    RetentionLineDTO,
} from "../../dto/HonorariumLiquidationDTO"
import { formatMoney } from "../../utils/money"
import { formatDateOnly } from "../../utils/date"
import useNotify from "../../hooks/useNotify"
import { confirmDelete } from "../../components/ConfirmDelete/confirmDelete"
import Kpi from "../../components/Kpi/Kpi"
import ReceiptFormsDialog from "../../components/ReceiptFormsDialog/ReceiptFormsDialog"
import SignedAmount from "../../components/HonorariumMovement/SignedAmount"
import RetentionLinesTable from "../../components/HonorariumMovement/RetentionLinesTable"
import AccountDebtLines from "../../components/HonorariumMovement/AccountDebtLines"
import ManualAdjustmentDialog from "../../components/ManualAdjustmentDialog/ManualAdjustmentDialog"
import AddAdministrativeChargeDialog from "./AddAdministrativeChargeDialog"
import AddItemsDialog from "./AddItemsDialog"
import LiquidationProfessionalStatusTag from "./LiquidationProfessionalStatusTag"
import LiquidationStatusTag from "./LiquidationStatusTag"
import RegisterPaymentDialog from "./RegisterPaymentDialog"

interface HonorariumLiquidationProfessionalViewProps {
    liquidationId: number;
    liquidationProfessionalId: number;
    onBack: () => void;
}

const HONORARIUM_GROUP = 1;
const WITHHOLDING_GROUP = 2;

/**
 * Aviso del excedente de gastos, débitos manuales y retenciones por facturación propia: con la liquidación abierta, lo
 * que va a pasar al cerrar; cerrada, lo que falta descontar.
 */
function chargesExcessNote(isOpen: boolean, excess: number, debtPending: number | null, withDebits: boolean,
                           withRetentions: boolean): string {
    const parts = ['gastos'];
    if (withDebits) parts.push('deducciones manuales');
    if (withRetentions) parts.push('retenciones por facturación propia');
    const what = parts.length === 1 ? 'Los gastos'
        : `Los ${parts.slice(0, -1).join(', ')} y ${parts[parts.length - 1]}`;
    if (isOpen) {
        return `${what} superan lo que cobra en ${formatMoney(excess)}. Al cerrar, el excedente se descuenta del saldo a favor `
            + 'y lo que no alcance queda como deuda en su cuenta corriente.';
    }
    if (debtPending != null && debtPending > 0) {
        return `${what} superaron lo que cobra en ${formatMoney(excess)}. Quedan ${formatMoney(debtPending)} como deuda `
            + 'en su cuenta corriente: se descuentan en la próxima liquidación o rendición.';
    }
    return `${what} superaron lo que cobra en ${formatMoney(excess)}. El excedente ya se descontó de su cuenta corriente.`;
}

/** Fila de la tabla de deducciones: las que calcula el cierre (anticipos, cuenta corriente) y las manuales. */
interface DeductionRow {
    key: string;
    date: string;
    label: string;
    amount: number;
    /** Solo las manuales se pueden quitar, con la liquidación abierta. */
    manual: HonorariumManualAdjustmentDTO | null;
}

function deductionRows(automatic: HonorariumLiquidationPaymentDTO[], manualDebits: HonorariumManualAdjustmentDTO[]): DeductionRow[] {
    return [
        ...automatic.map(row => ({
            key: `auto-${row.honorariumLiquidationPayment}`,
            date: row.date,
            label: row.label,
            amount: row.amount,
            manual: null,
        })),
        ...manualDebits.map(row => ({
            key: `manual-${row.honorariumLiquidationDetail}`,
            date: row.date,
            label: row.description,
            amount: row.amount,
            manual: row,
        })),
    ];
}

/**
 * Liquidación de un profesional (DS §3.7): totales, honorarios, retenciones, deducciones, créditos, gastos y pagos.
 * Todos los montos los calcula el backend; después de cada acción se vuelven a pedir.
 */
export default function HonorariumLiquidationProfessionalView({
    liquidationId,
    liquidationProfessionalId,
    onBack,
}: HonorariumLiquidationProfessionalViewProps) {
    const notify = useNotify();

    const [liquidation, setLiquidation] = useState<HonorariumLiquidationDTO | null>(null);
    const [professional, setProfessional] = useState<HonorariumLiquidationProfessionalDTO | null>(null);
    const [detail, setDetail] = useState<HonorariumLiquidationDetailResponseDTO | null>(null);
    const [deductions, setDeductions] = useState<HonorariumLiquidationPaymentDTO[]>([]);
    const [charges, setCharges] = useState<HonorariumAdministrativeChargeDTO[]>([]);
    const [adjustments, setAdjustments] = useState<HonorariumManualAdjustmentDTO[]>([]);
    const [adjustmentType, setAdjustmentType] = useState<ManualAdjustmentType | null>(null);
    const [paymentOrders, setPaymentOrders] = useState<HonorariumLiquidationPaymentOrderDTO[]>([]);
    const [credits, setCredits] = useState<HonorariumLiquidationAccountCreditDTO[]>([]);
    const [retentions, setRetentions] = useState<RetentionLineDTO[]>([]);
    const [accountDebts, setAccountDebts] = useState<AccountDebtLineDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [honorariumExpanded, setHonorariumExpanded] = useState<DataTableExpandedRows | DataTableValueArray>({});
    const [withholdingExpanded, setWithholdingExpanded] = useState<DataTableExpandedRows | DataTableValueArray>({});
    const [addItemsVisible, setAddItemsVisible] = useState(false);
    const [addChargeVisible, setAddChargeVisible] = useState(false);
    const [paying, setPaying] = useState<HonorariumLiquidationProfessionalDTO | null>(null);
    const [formsVisible, setFormsVisible] = useState(false);

    /** `silent`: recarga después de una acción, sin tapar la pantalla con el spinner. */
    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const [liquidationData, professionalData, detailData, deductionsData, chargesData, ordersData, creditsData, adjustmentsData, retentionsData, accountDebtsData] = await Promise.all([
                getLiquidation(liquidationId),
                getLiquidationProfessional(liquidationProfessionalId),
                getLiquidationDetails(liquidationProfessionalId),
                getLiquidationPayments(liquidationProfessionalId),
                getAdministrativeCharges(liquidationProfessionalId),
                getLiquidationPaymentOrders(liquidationProfessionalId),
                getAccountCredits(liquidationProfessionalId),
                getManualAdjustments(liquidationProfessionalId),
                getRetentions(liquidationProfessionalId),
                getAccountDebts(liquidationProfessionalId),
            ]);
            setLiquidation(liquidationData);
            setProfessional(professionalData);
            setDetail(detailData);
            setDeductions(deductionsData);
            setCharges(chargesData);
            setPaymentOrders(ordersData);
            setCredits(creditsData);
            setAdjustments(adjustmentsData);
            setRetentions(retentionsData);
            setAccountDebts(accountDebtsData);
        } catch (error: unknown) {
            const apiError = parseApiError(error);
            if (apiError.status === 404) {
                // Ya no está en la liquidación (por ejemplo, se quitaron todos sus ítems)
                notify.error(apiError.message);
                onBack();
                return;
            }
            notify.error('No se pudo cargar la liquidación del profesional. Intentá de nuevo.');
        } finally {
            setLoading(false);
        }
    }, [liquidationId, liquidationProfessionalId, notify, onBack]);

    useEffect(() => { load(); }, [load]);

    const isOpen = liquidation?.statusId === HonorariumLiquidationStatusId.OPEN;
    const honorariumGroups = detail?.groups.filter(g => g.detailType === HONORARIUM_GROUP) ?? [];
    const withholdingGroups = detail?.groups.filter(g => g.detailType === WITHHOLDING_GROUP) ?? [];
    const canPay = !isOpen && professional?.pendingAmount != null && professional.pendingAmount > 0;
    // Saldo a favor: comprobantes de la cuenta corriente sin pagar del todo (rendiciones, liquidaciones anteriores)
    const hasCredit = professional?.accountCredit != null && professional.accountCredit > 0;
    const manualDebits = adjustments.filter(a => a.type === 'DEBIT');
    const manualCredits = adjustments.filter(a => a.type === 'CREDIT');
    const hasManualCredits = (professional?.manualCreditsTotal ?? 0) > 0;
    // Retenciones por facturación propia: solo los que facturan por su cuenta. Negativas = se le devuelven (anulaciones)
    const retentionsTotal = professional?.retentionsTotal ?? 0;
    const hasRetentions = retentionsTotal !== 0 || retentions.length > 0;

    function handleRemoveItem(item: HonorariumLiquidationDetailDTO) {
        confirmDelete({
            header: '¿Quitar ítem?',
            message: `Se va a quitar «${item.description}» (${formatMoney(item.amount)}) de la liquidación. `
                + 'Vuelve a quedar pendiente para otra liquidación.',
            acceptLabel: 'Quitar',
            onAccept: async () => {
                try {
                    const { professionalRemoved } = await removeItemFromLiquidation(item.honorariumLiquidationDetail);
                    if (professionalRemoved) {
                        // Sin ítems de honorario el profesional sale de la liquidación: se vuelve al detalle
                        notify.success('Ítem quitado · el profesional salió de la liquidación');
                        onBack();
                        return;
                    }
                    notify.success('Ítem quitado');
                    load(true);
                } catch (error: unknown) {
                    notifyApiError(error, 'No se pudo quitar el ítem. Intentá de nuevo.');
                }
            },
        });
    }

    function handleRemoveRetentions() {
        confirmDelete({
            header: '¿Quitar retenciones?',
            message: `Se van a quitar las retenciones por facturación propia (${formatMoney(retentionsTotal)}). `
                + 'Vuelven a quedar pendientes para la próxima liquidación o rendición.',
            acceptLabel: 'Quitar',
            onAccept: async () => {
                try {
                    const { professionalRemoved } = await removeRetentions(liquidationProfessionalId);
                    if (professionalRemoved) {
                        // Sin honorarios ni retenciones el profesional sale de la liquidación: se vuelve al detalle
                        notify.success('Retenciones quitadas · el profesional salió de la liquidación');
                        onBack();
                        return;
                    }
                    notify.success('Retenciones quitadas');
                    load(true);
                } catch (error: unknown) {
                    notifyApiError(error, 'No se pudieron quitar las retenciones. Intentá de nuevo.');
                }
            },
        });
    }

    function handleRemoveCharge(charge: HonorariumAdministrativeChargeDTO) {
        confirmDelete({
            header: '¿Quitar gasto administrativo?',
            message: `Se va a quitar «${charge.description}» (${formatMoney(charge.amount)}). `
                + 'Deja de descontarse del neto.',
            acceptLabel: 'Quitar',
            onAccept: async () => {
                try {
                    await removeAdministrativeCharge(charge.honorariumLiquidationDetail);
                    notify.success('Gasto administrativo quitado');
                    load(true);
                } catch (error: unknown) {
                    notifyApiError(error, 'No se pudo quitar el gasto administrativo. Intentá de nuevo.');
                }
            },
        });
    }

    async function handleAddAdjustment(adjustment: ManualAdjustmentDTO) {
        // Los errores los muestra el diálogo (campos o mensaje de negocio)
        await addManualAdjustment({ ...adjustment, liquidationProfessionalId });
        notify.success(adjustment.type === 'DEBIT' ? 'Deducción agregada' : 'Crédito agregado');
        setAdjustmentType(null);
        load(true);
    }

    function handleRemoveAdjustment(adjustment: HonorariumManualAdjustmentDTO) {
        const isDebit = adjustment.type === 'DEBIT';
        confirmDelete({
            header: isDebit ? '¿Quitar deducción?' : '¿Quitar crédito?',
            message: `Se va a quitar «${adjustment.description}» (${formatMoney(adjustment.amount)}). `
                + (isDebit ? 'Deja de descontarse del neto.' : 'Deja de sumarse al neto.'),
            acceptLabel: 'Quitar',
            onAccept: async () => {
                try {
                    await removeManualAdjustment(adjustment.honorariumLiquidationDetail);
                    notify.success(isDebit ? 'Deducción quitada' : 'Crédito quitado');
                    load(true);
                } catch (error: unknown) {
                    notifyApiError(error, isDebit
                        ? 'No se pudo quitar la deducción. Intentá de nuevo.'
                        : 'No se pudo quitar el crédito. Intentá de nuevo.');
                }
            },
        });
    }

    function removeAdjustmentButton(adjustment: HonorariumManualAdjustmentDTO) {
        return (
            <Button
                text
                rounded
                icon="pi pi-times"
                className="md-icon-btn"
                aria-label={`Quitar ${adjustment.description}`}
                tooltip="Quitar"
                tooltipOptions={{ position: 'left' }}
                onClick={() => handleRemoveAdjustment(adjustment)}
            />
        );
    }

    /** Errores de negocio con el mensaje del backend; el resto, con el genérico de la acción. */
    function notifyApiError(error: unknown, fallback: string) {
        const apiError = parseApiError(error);
        const isBusinessError = apiError.status >= 400 && apiError.status < 500;
        notify.error(isBusinessError ? apiError.message : fallback);
    }

    function honorariumItems(group: HonorariumLiquidationDetailGroupDTO) {
        return (
            <div className="honorarium-liquidation__expansion">
                <DataTable value={group.children} dataKey="honorariumLiquidationDetail" className="crudTable" emptyMessage="Sin ítems.">
                    <Column field="description" header="Descripción" />
                    <Column header="Fecha" body={(row: HonorariumLiquidationDetailDTO) => formatDateOnly(row.date)} style={{ width: '120px' }} />
                    <Column
                        header="Honorario"
                        body={(row: HonorariumLiquidationDetailDTO) => <SignedAmount amount={row.amount} format={formatMoney} />}
                        className="text-right"
                        headerClassName="col-right"
                        style={{ width: '150px' }}
                    />
                    {isOpen && (
                        <Column
                            header=""
                            style={{ width: '64px' }}
                            className="text-center"
                            body={(row: HonorariumLiquidationDetailDTO) => (
                                <Button
                                    text
                                    rounded
                                    icon="pi pi-times"
                                    className="md-icon-btn"
                                    aria-label={`Quitar ${row.description}`}
                                    tooltip="Quitar"
                                    tooltipOptions={{ position: 'left' }}
                                    onClick={() => handleRemoveItem(row)}
                                />
                            )}
                        />
                    )}
                </DataTable>
            </div>
        );
    }

    function withholdingItems(group: HonorariumLiquidationDetailGroupDTO) {
        return (
            <div className="honorarium-liquidation__expansion">
                <DataTable value={group.children} dataKey="honorariumLiquidationDetail" className="crudTable" emptyMessage="Sin retenciones.">
                    <Column field="description" header="Descripción" />
                    <Column header="Fecha" body={(row: HonorariumLiquidationDetailDTO) => formatDateOnly(row.date)} style={{ width: '120px' }} />
                    <Column header="Base imponible" body={(row: HonorariumLiquidationDetailDTO) => formatMoney(row.taxBase)} className="text-right" headerClassName="col-right" />
                    <Column header="Alícuota" body={(row: HonorariumLiquidationDetailDTO) => row.taxRate != null ? `${row.taxRate}%` : '—'} className="text-center" headerClassName="col-center" />
                    <Column header="Retención" body={(row: HonorariumLiquidationDetailDTO) => formatMoney(row.taxAmount)} className="text-right" headerClassName="col-right" />
                </DataTable>
            </div>
        );
    }

    return (
        <div className="liquidation-professional">
            <div>
                <Button text icon="pi pi-angle-double-left" label="Volver" className="md-back-link" onClick={onBack} />
            </div>
            <h1 className="md-page-title">Liquidación{liquidation ? ` N° ${liquidation.liquidationNumber}` : ''}</h1>
            {liquidation && professional && (
                <p className="md-entity-subtitle liquidation-detail__subtitle">
                    {professional.professionalName} · {liquidation.billingEntityName}
                    <LiquidationStatusTag statusId={liquidation.statusId} status={liquidation.status} />
                    {!isOpen && <LiquidationProfessionalStatusTag statusId={professional.statusId} status={professional.status} />}
                </p>
            )}

            {loading && (
                <div className="md-loading" role="status">
                    <ProgressSpinner strokeWidth="4" />
                    <span>Cargando…</span>
                </div>
            )}

            {!loading && liquidation && professional && (
                <>
                    <div className="md-kpi-row liquidation-detail__kpis">
                        <Kpi label="Honorarios" value={formatMoney(professional.totalAmount)} />
                        <Kpi label="Retenciones" value={formatMoney(professional.withholdingsTotal)} />
                        {hasManualCredits && <Kpi label="Créditos" value={formatMoney(professional.manualCreditsTotal)} />}
                        <Kpi label="Deducciones" value={formatMoney(professional.deductionsTotal)} />
                        <Kpi label="Gastos administrativos" value={formatMoney(professional.adminChargesTotal)} />
                        {hasRetentions && (
                            <Kpi
                                label={retentionsTotal < 0 ? 'Devolución de retenciones' : 'Retención fact. propia'}
                                value={formatMoney(new Big(retentionsTotal).abs())}
                                hint="De las órdenes que facturó él"
                            />
                        )}
                        <Kpi label="Neto a pagar" value={formatMoney(professional.netToPay)} hint={isOpen ? 'Proyectado al cierre' : undefined} />
                        {!isOpen && <Kpi label="Pagado" value={formatMoney(professional.totalPaymentOrders)} />}
                        {!isOpen && <Kpi label="Pendiente de pago" value={formatMoney(professional.pendingAmount)} />}
                        {hasCredit && <Kpi label="Saldo a favor C/C" value={formatMoney(professional.accountCredit)} hint="De comprobantes anteriores" />}
                        {hasCredit && !isOpen && <Kpi label="Total adeudado" value={formatMoney(professional.totalOwed)} />}
                    </div>

                    {/* Pagos de más que el cierre va a descontar; lo que se le debe por otros comprobantes va en "Saldo a favor" */}
                    {isOpen && professional.accountDebt != null && (
                        <p className="md-help liquidation-professional__note">
                            <i className="pi pi-info-circle" aria-hidden="true" />
                            Deuda en cuenta corriente: {formatMoney(professional.accountDebt)}. Se descuenta al cerrar la liquidación.
                        </p>
                    )}
                    {/* De dónde sale esa deuda: pagos de más, retenciones o gastos de liquidaciones y rendiciones anteriores */}
                    {isOpen && accountDebts.length > 0 && <AccountDebtLines lines={accountDebts} />}

                    {/* Gastos que superan lo que cobra: el cierre los registra como deuda en la cuenta corriente */}
                    {professional.adminChargesExcess != null && (
                        <p className="md-help liquidation-professional__note">
                            <i className="pi pi-info-circle" aria-hidden="true" />
                            {chargesExcessNote(isOpen, professional.adminChargesExcess, professional.adminChargesDebtPending,
                                (professional.manualDebitsTotal ?? 0) > 0, retentionsTotal > 0)}
                        </p>
                    )}

                    {hasCredit && (
                        <section className="liquidation-section">
                            <div className="liquidation-section__header">
                                <h2 className="md-section-title">Saldo a favor de cuenta corriente</h2>
                            </div>
                            <p className="md-help">
                                {isOpen
                                    ? 'Lo que se le debe por otros comprobantes. Después del cierre se puede pagar junto con esta liquidación.'
                                    : 'Lo que se le debe por otros comprobantes. Se puede pagar junto con esta liquidación.'}
                            </p>
                            <DataTable value={credits} dataKey="administrativeForm" className="crudTable" emptyMessage="Sin saldo a favor.">
                                <Column field="origin" header="Origen" />
                                <Column header="Fecha" body={(row: HonorariumLiquidationAccountCreditDTO) => formatDateOnly(row.date)} style={{ width: '120px' }} />
                                <Column header="Pendiente" body={(row: HonorariumLiquidationAccountCreditDTO) => formatMoney(row.pendingAmount)} className="text-right" headerClassName="col-right" style={{ width: '170px' }} />
                            </DataTable>
                        </section>
                    )}

                    <section className="liquidation-section">
                        <div className="liquidation-section__header">
                            <h2 className="md-section-title">Honorarios</h2>
                            {isOpen && (
                                <Button outlined icon="pi pi-plus-circle" label="Agregar ítems" className="md-ghost" onClick={() => setAddItemsVisible(true)} />
                            )}
                        </div>
                        <DataTable
                            value={honorariumGroups}
                            dataKey="groupLabel"
                            expandedRows={honorariumExpanded}
                            onRowToggle={(e) => setHonorariumExpanded(e.data)}
                            rowExpansionTemplate={honorariumItems}
                            className="crudTable"
                            emptyMessage="Sin honorarios cargados."
                        >
                            <Column expander style={{ width: '48px' }} />
                            <Column field="groupLabel" header="Origen" />
                            <Column header="Ítems" body={(g: HonorariumLiquidationDetailGroupDTO) => g.children.length} className="text-center" headerClassName="col-center" style={{ width: '100px' }} />
                            <Column header="Subtotal" body={(g: HonorariumLiquidationDetailGroupDTO) => formatMoney(g.totalAmount)} className="text-right" headerClassName="col-right" style={{ width: '170px' }} />
                        </DataTable>
                    </section>

                    <section className="liquidation-section">
                        <div className="liquidation-section__header">
                            <h2 className="md-section-title">Retenciones</h2>
                        </div>
                        <DataTable
                            value={withholdingGroups}
                            dataKey="groupLabel"
                            expandedRows={withholdingExpanded}
                            onRowToggle={(e) => setWithholdingExpanded(e.data)}
                            rowExpansionTemplate={withholdingItems}
                            className="crudTable"
                            emptyMessage="Sin retenciones."
                        >
                            <Column expander style={{ width: '48px' }} />
                            <Column field="groupLabel" header="Impuesto" />
                            <Column header="Retenido" body={(g: HonorariumLiquidationDetailGroupDTO) => formatMoney(g.totalTaxAmount)} className="text-right" headerClassName="col-right" style={{ width: '170px' }} />
                        </DataTable>
                    </section>

                    <section className="liquidation-section">
                        <div className="liquidation-section__header">
                            <h2 className="md-section-title">Deducciones</h2>
                            {isOpen && (
                                <Button outlined icon="pi pi-minus-circle" label="Agregar deducción" className="md-ghost" onClick={() => setAdjustmentType('DEBIT')} />
                            )}
                        </div>
                        <DataTable value={deductionRows(deductions, manualDebits)} dataKey="key" className="crudTable" emptyMessage="Sin deducciones.">
                            <Column header="Fecha" body={(row: DeductionRow) => formatDateOnly(row.date)} style={{ width: '120px' }} />
                            <Column header="Descripción" body={(row: DeductionRow) => row.label || '—'} />
                            <Column header="Monto" body={(row: DeductionRow) => formatMoney(row.amount)} className="text-right" headerClassName="col-right" style={{ width: '170px' }} />
                            {isOpen && (
                                <Column
                                    header=""
                                    style={{ width: '64px' }}
                                    className="text-center"
                                    body={(row: DeductionRow) => row.manual ? removeAdjustmentButton(row.manual) : null}
                                />
                            )}
                        </DataTable>
                    </section>

                    {(isOpen || manualCredits.length > 0) && (
                        <section className="liquidation-section">
                            <div className="liquidation-section__header">
                                <h2 className="md-section-title">Créditos</h2>
                                {isOpen && (
                                    <Button outlined icon="pi pi-plus-circle" label="Agregar crédito" className="md-ghost" onClick={() => setAdjustmentType('CREDIT')} />
                                )}
                            </div>
                            <DataTable value={manualCredits} dataKey="honorariumLiquidationDetail" className="crudTable" emptyMessage="Sin créditos.">
                                <Column header="Fecha" body={(row: HonorariumManualAdjustmentDTO) => formatDateOnly(row.date)} style={{ width: '120px' }} />
                                <Column header="Descripción" body={(row: HonorariumManualAdjustmentDTO) => row.description || '—'} />
                                <Column header="Monto" body={(row: HonorariumManualAdjustmentDTO) => formatMoney(row.amount)} className="text-right" headerClassName="col-right" style={{ width: '170px' }} />
                                {isOpen && (
                                    <Column
                                        header=""
                                        style={{ width: '64px' }}
                                        className="text-center"
                                        body={(row: HonorariumManualAdjustmentDTO) => removeAdjustmentButton(row)}
                                    />
                                )}
                            </DataTable>
                        </section>
                    )}

                    <section className="liquidation-section">
                        <div className="liquidation-section__header">
                            <h2 className="md-section-title">Gastos administrativos</h2>
                            {isOpen && (
                                <Button outlined icon="pi pi-plus-circle" label="Agregar gasto" className="md-ghost" onClick={() => setAddChargeVisible(true)} />
                            )}
                        </div>
                        <DataTable value={charges} dataKey="honorariumLiquidationDetail" className="crudTable" emptyMessage="Sin gastos administrativos.">
                            <Column header="Fecha" body={(row: HonorariumAdministrativeChargeDTO) => formatDateOnly(row.date)} style={{ width: '120px' }} />
                            <Column header="Descripción" body={(row: HonorariumAdministrativeChargeDTO) => row.description || '—'} />
                            <Column header="Monto" body={(row: HonorariumAdministrativeChargeDTO) => formatMoney(row.amount)} className="text-right" headerClassName="col-right" style={{ width: '170px' }} />
                            {isOpen && (
                                <Column
                                    header=""
                                    style={{ width: '64px' }}
                                    className="text-center"
                                    body={(row: HonorariumAdministrativeChargeDTO) => (
                                        <Button
                                            text
                                            rounded
                                            icon="pi pi-times"
                                            className="md-icon-btn"
                                            aria-label={`Quitar el gasto ${row.description}`}
                                            tooltip="Quitar"
                                            tooltipOptions={{ position: 'left' }}
                                            onClick={() => handleRemoveCharge(row)}
                                        />
                                    )}
                                />
                            )}
                        </DataTable>
                    </section>

                    {hasRetentions && (
                        <section className="liquidation-section">
                            <div className="liquidation-section__header">
                                <h2 className="md-section-title">Retenciones por facturación propia</h2>
                                {isOpen && (
                                    <Button outlined icon="pi pi-minus-circle" label="Quitar retenciones" className="md-ghost" onClick={handleRemoveRetentions} />
                                )}
                            </div>
                            <p className="md-help">
                                Lo que no le corresponde al profesional de las órdenes que facturó él, según su contrato de distribución.
                                Se descuenta como los gastos administrativos.
                            </p>
                            <RetentionLinesTable lines={retentions} />
                        </section>
                    )}

                    {!isOpen && (
                        <section className="liquidation-section">
                            <div className="liquidation-section__header">
                                <h2 className="md-section-title">Pagos</h2>
                                <div className="md-dialog__footer">
                                    {professional.internalReceiptForm != null && (
                                        <Button outlined icon="pi pi-file" label="Ver comprobantes" className="md-ghost" onClick={() => setFormsVisible(true)} />
                                    )}
                                    {canPay && (
                                        <Button icon="pi pi-wallet" label="Registrar pago" className="md-primary" onClick={() => setPaying(professional)} />
                                    )}
                                </div>
                            </div>
                            <DataTable value={paymentOrders} dataKey="honorariumLiquidationPaymentOrder" className="crudTable" emptyMessage="Todavía no hay pagos registrados.">
                                <Column header="Fecha" body={(row: HonorariumLiquidationPaymentOrderDTO) => formatDateOnly(row.date)} style={{ width: '120px' }} />
                                <Column header="Medio de pago" body={(row: HonorariumLiquidationPaymentOrderDTO) => row.paymentOrder
                                    ? row.paymentMethodName || '—'
                                    : 'Compensación con comprobante de gastos'} />
                                <Column header="Monto" body={(row: HonorariumLiquidationPaymentOrderDTO) => formatMoney(row.amount)} className="text-right" headerClassName="col-right" style={{ width: '170px' }} />
                            </DataTable>
                        </section>
                    )}
                </>
            )}

            <AddItemsDialog
                visible={addItemsVisible}
                liquidationId={liquidationId}
                professionalId={professional?.professional}
                onHide={() => setAddItemsVisible(false)}
                onAdded={() => {
                    setAddItemsVisible(false);
                    load(true);
                }}
            />
            <AddAdministrativeChargeDialog
                visible={addChargeVisible}
                liquidationProfessionalId={liquidationProfessionalId}
                onHide={() => setAddChargeVisible(false)}
                onAdded={() => {
                    setAddChargeVisible(false);
                    load(true);
                }}
            />
            <ManualAdjustmentDialog
                visible={adjustmentType != null}
                type={adjustmentType ?? 'DEBIT'}
                onHide={() => setAdjustmentType(null)}
                onSubmit={handleAddAdjustment}
            />
            <ReceiptFormsDialog
                visible={formsVisible}
                header={professional ? `Comprobantes · ${professional.professionalName}` : 'Comprobantes'}
                load={() => getLiquidationReceiptForms(liquidationProfessionalId)}
                summaryFileName={`resumen-liquidacion-${liquidation?.liquidationNumber ?? ''}-${professional?.professionalName ?? ''}.pdf`}
                onHide={() => setFormsVisible(false)}
            />
            <RegisterPaymentDialog
                professional={paying}
                liquidationNumber={liquidation?.liquidationNumber ?? 0}
                onHide={() => setPaying(null)}
                onRegistered={() => {
                    setPaying(null);
                    load(true);
                }}
            />
        </div>
    );
}
